import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../../../common/components/Icon/Icon";

interface CheckoutStepActionsProps {
  /** Function to handle the "Previous/Back" action. */
  onPrevious?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Function to handle the "Next/Submit" action. Usually handled by form onSubmit, but can be overridden. */
  onNext?: (e: React.MouseEvent<HTMLButtonElement>) => void;

  /** Custom text for the Previous button. Defaults to "Back". */
  previousButtonText?: string;
  /** Text for the Next button. */
  nextButtonText: string;

  /** Whether the form is currently processing (loading state). */
  isProcessing: boolean;
  /** Whether to disable the Next button (e.g. validation error). */
  isNextDisabled?: boolean;
  /** Whether to disable the Prev button. */
  isPrevDisabled?: boolean;

  /** Whether to show the Next button. Defaults to true. */
  showNextButton?: boolean;
  /** Whether to show the Previous button. Defaults to true if onPrevious is provided. */
  showPrevButton?: boolean;

  /** HTML button type for Previous. Defaults to 'button'. */
  prevButtonType?: "button" | "submit";
  /** HTML button type for Next. Defaults to 'submit'. */
  nextButtonType?: "button" | "submit";
}

const CheckoutStepActions: React.FC<CheckoutStepActionsProps> = ({
  onPrevious,
  onNext,
  previousButtonText,
  nextButtonText,
  isProcessing,
  isNextDisabled = false,
  isPrevDisabled = false,
  showNextButton = true,
  showPrevButton = true,
  prevButtonType = "button",
  nextButtonType = "submit",
}) => {
  const { t } = useTranslation();

  // Local state to track which button initiated the action
  const [loadingButton, setLoadingButton] = useState<"prev" | "next" | null>(
    null
  );

  // Reset local state when the global processing state finishes
  useEffect(() => {
    if (!isProcessing) {
      setLoadingButton(null);
    }
  }, [isProcessing]);

  const handlePrevClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isProcessing) return;
    setLoadingButton("prev");
    onPrevious?.(e);
  };

  const handleNextClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isProcessing) return;
    setLoadingButton("next");
    onNext?.(e);
  };

  // Determine if the NEXT button should show loader
  // If isProcessing is true AND it wasn't the Prev button that was clicked,
  // show the loader on Next.
  const isNextLoading = isProcessing && loadingButton !== "prev";

  // Prev button never shows loader, just gets disabled.

  return (
    <div className="checkout-step__actions">
      {/* Previous Button */}
      {showPrevButton && (
        <button
          type={prevButtonType}
          className="checkout-step__prev-btn"
          onClick={handlePrevClick}
          disabled={isProcessing || isPrevDisabled}
        >
          <Icon iconName="arrow-left" />
          <span>
            {previousButtonText ||
              t("checkoutForm.actions.previous", "Previous")}
          </span>
        </button>
      )}

      {/* Next Button */}
      {showNextButton && (
        <button
          type={nextButtonType}
          className="checkout-step__submit-btn"
          onClick={handleNextClick}
          disabled={isProcessing || isNextDisabled}
        >
          {isNextLoading ? (
            <>
              <span className="loader-spinner"></span>
              <span>
                {t("checkoutForm.actions.processing", "Processing...")}
              </span>
            </>
          ) : (
            <>
              <span>{nextButtonText}</span>
              <Icon iconName="arrow-right" />
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default CheckoutStepActions;
